import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { API_URL } from "../../utils/constant";

const RedTeamingAssets = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAsset, setNewAsset] = useState({
    name: "",
    type: "",
    description: "",
    url: ""
  });

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/red-teaming/assets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assets');
      }

      const data = await response.json();
      setAssets(data);
    } catch (error) {
      // console.error removed
      toastr.error('Error', 'Failed to load assets');
      
      // Fallback to mock data
      setAssets([
        {
          id: 1,
          name: "api.domain.com",
          type: "API Server",
          description: "Main API endpoint for the application",
          url: "https://api.domain.com",
          status: "active"
        },
        {
          id: 2,
          name: "dev.site.io",
          type: "Development Server",
          description: "Development environment server",
          url: "https://dev.site.io",
          status: "active"
        },
        {
          id: 3,
          name: "login.company.com",
          type: "Authentication Server",
          description: "User authentication and authorization service",
          url: "https://login.company.com",
          status: "active"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const createAsset = async (assetData) => {
    try {
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/red-teaming/assets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assetData)
      });

      if (!response.ok) {
        throw new Error('Failed to create asset');
      }

      const newAsset = await response.json();
      setAssets(prev => [newAsset, ...prev]);
      setShowCreateModal(false);
      setNewAsset({ name: "", type: "", description: "", url: "" });
      toastr.success('Success', 'Asset created successfully');
    } catch (error) {
      // console.error removed
      toastr.error('Error', 'Failed to create asset');
    }
  };

  const deleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/red-teaming/assets/${assetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete asset');
      }

      setAssets(prev => prev.filter(asset => asset.id !== assetId));
      toastr.success('Success', 'Asset deleted successfully');
    } catch (error) {
      // console.error removed
      toastr.error('Error', 'Failed to delete asset');
    }
  };

  const handleCreateAsset = () => {
    if (!newAsset.name || !newAsset.type) {
      toastr.error('Error', 'Please fill in all required fields');
      return;
    }

    createAsset(newAsset);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box">
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/red-teaming">Red Teaming</Link>
                </li>
                <li className="breadcrumb-item active">Assets</li>
              </ol>
            </div>
            <h4 className="page-title">Red Teaming Assets</h4>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <h4 className="header-title">Assets</h4>
                </div>
                <div className="col-md-6 text-right">
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <i className="fa fa-plus mr-2"></i>
                    Add Asset
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>URL</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((asset) => (
                      <tr key={asset.id}>
                        <td>{asset.name}</td>
                        <td>{asset.type}</td>
                        <td>
                          <a href={asset.url} target="_blank" rel="noopener noreferrer">
                            {asset.url}
                          </a>
                        </td>
                        <td>{asset.description}</td>
                        <td>
                          <span className={`badge badge-${asset.status === 'active' ? 'success' : 'secondary'}`}>
                            {asset.status}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group">
                            <button className="btn btn-sm btn-info">
                              <i className="fa fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteAsset(asset.id)}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Asset Modal */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Asset</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowCreateModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Asset Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newAsset.name}
                    onChange={(e) => setNewAsset({...newAsset, name: e.target.value})}
                    placeholder="e.g., api.domain.com"
                  />
                </div>
                <div className="form-group">
                  <label>Asset Type *</label>
                  <select
                    className="form-control"
                    value={newAsset.type}
                    onChange={(e) => setNewAsset({...newAsset, type: e.target.value})}
                  >
                    <option value="">Select Type</option>
                    <option value="API Server">API Server</option>
                    <option value="Web Server">Web Server</option>
                    <option value="Database Server">Database Server</option>
                    <option value="Authentication Server">Authentication Server</option>
                    <option value="Development Server">Development Server</option>
                    <option value="Staging Server">Staging Server</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={newAsset.url}
                    onChange={(e) => setNewAsset({...newAsset, url: e.target.value})}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={newAsset.description}
                    onChange={(e) => setNewAsset({...newAsset, description: e.target.value})}
                    placeholder="Brief description of the asset"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateAsset}
                >
                  Add Asset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedTeamingAssets; 